import type { CoordinateFormat } from '@gmn-portal/backend';
import { Download } from 'lucide-react';
import { DateTime } from 'luxon';
import { toast } from 'sonner';
import { useIntl } from '@/core/i18n/use-intl';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import {
  buildPositionsCsv,
  type CsvDialect,
  downloadCsvFile,
  type PositionsCsvEntry,
  toCsvFileName,
} from '@/shared/utils';

type PositionsCsvExportButtonProps = {
  positions: PositionsCsvEntry[];
  coordinateFormat: CoordinateFormat;
  /** Label of the positions field, used for the file name. */
  fieldLabel: string;
  /** Name of the object/contract the field belongs to, used for the file name. */
  entityName?: string | null;
  /** Status column header; when empty the field has no per-position status. */
  statusLabel?: string;
  testId?: string;
};

export function PositionsCsvExportButton({
  positions,
  coordinateFormat,
  fieldLabel,
  entityName,
  statusLabel,
  testId = 'positions-csv-export',
}: PositionsCsvExportButtonProps) {
  const { t } = useIntl();

  const handleExport = (dialect: CsvDialect) => {
    try {
      const csv = buildPositionsCsv(positions, {
        coordinateFormat,
        dialect,
        labels: {
          label: t('metadata.positions.label'),
          latitude: t('metadata.positions.latitude'),
          longitude: t('metadata.positions.longitude'),
          latitudeDecimal: t('metadata.positions.latitudeDecimal'),
          longitudeDecimal: t('metadata.positions.longitudeDecimal'),
          ...(statusLabel ? { status: statusLabel } : {}),
          yes: t('common.yes'),
          no: t('common.no'),
        },
      });

      downloadCsvFile(
        toCsvFileName([entityName, fieldLabel, DateTime.now().toFormat('yyyy-MM-dd')]),
        csv
      );
      toast.success(t('metadata.positions.exportSuccess'));
    } catch {
      toast.error(t('metadata.positions.exportError'));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="secondary" size="sm" className="h-8" data-testid={testId}>
          <Download className="size-3.5 mr-1.5" />
          {t('metadata.positions.exportCsv')}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {t('metadata.positions.exportCsvFormat')}
        </DropdownMenuLabel>
        <DropdownMenuItem
          onSelect={() => handleExport('excel-de')}
          data-testid={`${testId}-excel-de`}
          className="flex-col items-start gap-0.5"
        >
          <span className="text-sm">{t('metadata.positions.exportCsvExcel')}</span>
          <span className="text-xs text-muted-foreground">
            {t('metadata.positions.exportCsvExcelHint')}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => handleExport('standard')}
          data-testid={`${testId}-standard`}
          className="flex-col items-start gap-0.5"
        >
          <span className="text-sm">{t('metadata.positions.exportCsvStandard')}</span>
          <span className="text-xs text-muted-foreground">
            {t('metadata.positions.exportCsvStandardHint')}
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
